import React from "react"
import styled, { css } from "styled-components"
import { colors } from "buoy-theme"
import { Heading } from "./Card"
import Text from "../../common/components/Text"
import DiagnosisContainer from "./DiagnosisContainer"
import iconAlert from "../../../static/images/icon-alert.svg"
import ScrollTracker from "../../interview/components/ScrollTracker"
import { injectIntl, defineMessages, FormattedMessage } from "react-intl"

const AlarmHeading = styled(Heading)`
  padding-right: 40px;
`
const AlarmImage = styled.img`
  position: absolute;
  right: 0%;
  top: 0%;
`

const AlarmBox = styled.div`
  background-color: ${props =>
    props.level > 5 ? "rgba(255, 61, 73, 0.1)" : ""};
  border-radius: 4px;
  width: 100%;
  padding: ${props => (props.level > 5 ? "10px 14px" : "")};
`

const AlarmText = styled(Text)`
  align-items: center;
  text-align: left;
  width: 100%;
  ${props => (props.nth ? "padding-bottom: 16px" : "padding: 16px 0")};
`

const AlarmBoxText = styled(AlarmText)`
  color: ${props => (props.level > 5 ? "#ff3d49" : "")};
`

const AlarmDescriptionText = styled(AlarmText)`
  ${props =>
    props.border
      ? css`
          border-top: solid 1px ${props => props.border};
        `
      : ""};
  padding-bottom: 0px;
`

const Header = (level, genderPov, formatMessage) => {
  const headerMessages = defineMessages({
    t1: {
      id: "triage.t1.header",
      defaultMessage:
        "Self-isolate and keep monitoring { genderPov, select, maleself {your} maleother {his} femaleself {your} femaleother {her} } symptoms",
    },
    t2: {
      id: "triage.t2.header",
      defaultMessage:
        "Self-isolate and ask { genderPov, select, maleself {your} maleother {his} femaleself {your} femaleother {her} } doctor about getting tested",
    },
  })
  switch (level) {
    case 1:
      return formatMessage(headerMessages.t1, { genderPov })
    case 2:
      return formatMessage(headerMessages.t2, { genderPov })
    case 5:
      return (
        <FormattedMessage
          id="triage.t5.header"
          defaultMessage="Contact a medical professional today"
        />
      )
    case 6:
      return (
        <FormattedMessage
          id="triage.t6.header"
          defaultMessage="Seek emergency medical treatment"
        />
      )
    case 7:
      return (
        <FormattedMessage id="triage.t7.header" defaultMessage="Call 911" />
      )
    default:
      return (
        <FormattedMessage
          id="triage.t1.header"
          defaultMessage="Possible coronavirus infection"
        />
      )
  }
}

const RecommendationParagraph = (level, genderPov, formatMessage) => {
  const recommendationMessages = defineMessages({
    t1: {
      id: "triage.t1.recommendation.paragraph",
      defaultMessage:
        "Keep monitoring { genderPov, select, maleself {your} maleother {his} femaleself {your} femaleother {her} } symptoms, and if they get worse, check back in with Buoy.",
    },
  })
  switch (level) {
    case 1: 
      return formatMessage(recommendationMessages.t1, { genderPov }) 
    case 2:
      return (
        <FormattedMessage
          id="triage.t2.recommendation.paragraph"
          defaultMessage="Expect longer-than-average wait times and limited supply of tests in most areas."
        />
      )
    case 5:
      return (
        <FormattedMessage
          id="triage.t5.recommendation.paragraph"
          defaultMessage="We recommend contacting a medical professional today."
        />
      )
    case 6:
      return (
        <FormattedMessage
          id="triage.t6.recommendation.paragraph"
          defaultMessage="We recommend visiting your local emergency room."
        />
      )
    case 7:
      return (
        <FormattedMessage
          id="triage.t7.recommendation.paragraph"
          defaultMessage="We recommend emergency medical transport."
        />
      )
    default:
      return (
        <FormattedMessage
          id="triage.t7.recommendation.paragraph"
          defaultMessage="We recommend emergency medical transport."
        />
      )
  }
}

const AlarmDescriptionContent = (triage, genderPov, formatMessage) => {
  const recommendationMessages = defineMessages({
    t1: {
      id: "triage.t1.description",
      defaultMessage:
        "If { genderPov, select, maleself {you} maleother {he} femaleself {you} femaleother {she} } start{ genderPov, select, maleself {} maleother {s} femaleself {s} femaleother {} } experiencing other symptoms related to coronavirus such as difficulty breathing or chest pain, contact { genderPov, select, maleself {your} maleother {his} femaleself {your} femaleother {her} } doctor.",
    },
    t6: {
      id: "triage.t6.description",
      defaultMessage:
        "Call ahead so they can prepare for { genderPov, select, maleself {your} maleother {his} femaleself {your} femaleother {her} } arrival. If { genderPov, select, maleself {your} maleother {his} femaleself {your} femaleother {her} } condition is contagious, this can help prevent it from spreading.",
    },
    t7: {
      id: "triage.t7.description",
      defaultMessage:
        "Call 911 and mention { genderPov, select, maleself {you} maleother {he} femaleself {you} femaleother {she} } may have coronavirus so they can prepare.",
    },
  })
  switch (triage.level) {
    case 1:
      return formatMessage(recommendationMessages.t1, { genderPov })
    case 2:
    case 5:
      return (
        <FormattedMessage
          id="triage.t5.description"
          defaultMessage="If this feels like an emergency, visit the nearest emergency room."
        />
      )
    case 6:
      return formatMessage(recommendationMessages.t6, { genderPov })
    case 7:
      return formatMessage(recommendationMessages.t7, { genderPov })
    default:
      return (
        <FormattedMessage
          id="triage.default.description"
          defaultMessage="If this feels like an emergency, visit the nearest emergency room."
        />
      )
  }
}

const Alarm = ({ triage, identifiers, intl }) => {
  const { formatMessage } = intl
  const genderPov = `${identifiers.sex}${identifiers.pov}`

  return (
    <ScrollTracker sectionTitle="Triage alarm">
      <DiagnosisContainer>
        <AlarmHeading>
          {Header(triage.level, genderPov, formatMessage)}
          {triage.level > 4 && <AlarmImage src={iconAlert} />}
        </AlarmHeading>

        {"diagnosis" in triage.potential_diagnosis && (
          <AlarmBox level={triage.level}>
            <AlarmBoxText level={triage.level}>
              {triage.explanation}
            </AlarmBoxText>
          </AlarmBox>
        )}

        <AlarmText>
          {RecommendationParagraph(triage.level, genderPov, formatMessage)}
        </AlarmText>
        {triage.level === 5 && (
          <AlarmText nth={true}>
            <FormattedMessage
              id="triage.t5.recommendation.paragraph-2"
              defaultMessage="Expect longer-than-average wait times and limited supply of tests in most areas."
            />
          </AlarmText>
        )}

        <AlarmDescriptionText border={colors.neutralLight}>
          {AlarmDescriptionContent(triage, genderPov, formatMessage)}
        </AlarmDescriptionText>
      </DiagnosisContainer>
    </ScrollTracker>
  )
}

export default injectIntl(Alarm)



// WEBPACK FOOTER //
// ./assets/completion/components/Alarm.js